import { getReceiptDate } from "../../utils/receiptDate";
import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  TextField,
  Snackbar,
  Alert,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import jsQR from "jsqr";

// Base receipt template without items
const receiptTemplate = {
  id: null,
  soPhieu: "",
  ngay: new Date(),
  khachHang: "",
  trangThai: "Chờ xác nhận",
  tongSoLuong: 0,
  items: [],
};

const NewDieuChuyenTT = () => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [storeName, setStoreName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [stores, setStores] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [note, setNote] = useState("");
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [manualQrInput, setManualQrInput] = useState("");
  const [qrProcessing, setQrProcessing] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedItems, setHighlightedItems] = useState([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const bottomRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const qrInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [storeSearchTerm, setStoreSearchTerm] = useState("");

  // Fetch stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch("https://doticom.vn:3000/stores");
        if (!response.ok) throw new Error("API error");
        const storesData = await response.json();
        setStores(storesData);
      } catch (e) {
        setSnackbarMessage("Không thể tải danh sách cửa hàng");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchStores();
  }, []);

  // Init receipt
  useEffect(() => {
    const nextId = Math.floor(Math.random() * 1000000);
    setReceipt({
      ...receiptTemplate,
      ngay: new Date(),
      id: nextId,
      soPhieu: `DTTT/BN-${String(nextId).padStart(7, "0")}`,
      khachHang: "",
      items: [],
      ghiChu: "",
    });
    setNote("");
    setLoading(false);
    setIsAdmin(localStorage.getItem("isAdmin") === "true");
  }, []);

  // QR scan handler
  const processQRCode = async (qrData) => {
    setQrProcessing(true);
    try {
      let loSX = "";
      let quantity = 1; // Default quantity
      
      // Parse quantity and LoSX from input string
      if (qrData.includes("doticom.vn/t.php?c=")) {
        // Check if there's a quantity prefix (e.g., "10https://doticom.vn/t.php?c=509")
        const urlMatch = qrData.match(/(\d*)https?:\/\/doticom\.vn\/t\.php\?c=(\d+)/);
        if (urlMatch) {
          const quantityPrefix = urlMatch[1];
          loSX = urlMatch[2];
          if (quantityPrefix && quantityPrefix.length > 0) {
            quantity = parseInt(quantityPrefix, 10) || 1;
          }
        } else {
          // Fallback to original URL parsing
          const match = qrData.match(/c=(\d+)/);
          if (match) loSX = match[1];
        }
      } else if (/^\d+$/.test(qrData.trim())) {
        // Pure numeric string (LoSX only)
        loSX = qrData.trim();
        quantity = 1;
      } else {
        // Check if it's a quantity + LoSX combination without URL
        const numericMatch = qrData.match(/^(\d+)(\d{2,})$/);
        if (numericMatch && numericMatch[2].length >= 2) {
          // If second group has at least 2 digits, treat first as quantity
          quantity = parseInt(numericMatch[1], 10) || 1;
          loSX = numericMatch[2];
        } else {
          // Fallback: treat entire string as LoSX
          loSX = qrData.trim();
        }
      }
      
      if (!loSX) throw new Error("Không thể trích xuất mã LoSX từ QR code");

      // Call QRTrungThu API
      const qrRes = await fetch(
        `https://doticom.vn:3000/api/QRTrungThu?LoSX=${loSX}`
      );
      if (!qrRes.ok) throw new Error("Lỗi API QRTrungThu");
      let qrItems = await qrRes.json();

      // Ensure qrItems is always an array
      if (!Array.isArray(qrItems)) {
        if (qrItems && typeof qrItems === "object") {
          qrItems = [qrItems];
        } else {
          qrItems = [];
        }
      }

      if (!qrItems || qrItems.length === 0)
        throw new Error("Không tìm thấy sản phẩm với mã LoSX này");

      for (const qrItem of qrItems) {
        const { MaSP, DonGia } = qrItem;
        const itemRes = await fetch(
          `https://doticom.vn:3000/items/${MaSP}`
        );
        const itemData = itemRes.ok ? await itemRes.json() : [];
        const Ten_SP = itemData[0]?.Ten_SP || `Sản phẩm ${MaSP}`;
        setReceipt((prev) => {
          if (!prev) return prev;
          const idx = prev.items.findIndex((item) => item.maVt === MaSP);
          let updatedItems;
          if (idx >= 0) {
            updatedItems = [...prev.items];
            updatedItems[idx] = {
              ...updatedItems[idx],
              soLuong: (parseInt(updatedItems[idx].soLuong) || 0) + quantity,
              // Keep the existing LoSX if product already exists, or update if it's the same LoSX
              loSX: updatedItems[idx].loSX || loSX,
            };
          } else {
            updatedItems = [
              ...prev.items,
              {
                maVt: MaSP,
                ten: Ten_SP,
                soLuong: quantity,
                dvt: "cái",
                giaMua: DonGia || 0,
                isQRAdded: true,
                loSX: loSX, // Store LoSX for this specific product
              },
            ];
          }
          return { ...prev, items: updatedItems };
        });
      }
      setSnackbarMessage(
        `Đã thêm ${qrItems.length} sản phẩm với số lượng ${quantity} từ QR code (LoSX: ${loSX})`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      setErrorModalMessage(`Lỗi xử lý QR code: ${error.message}`);
      setErrorModalOpen(true);
    } finally {
      setQrProcessing(false);
    }
  };

  // Manual QR input submit
  const handleManualQRSubmit = () => {
    if (!manualQrInput.trim()) {
      setErrorModalMessage("Vui lòng nhập mã QR");
      setErrorModalOpen(true);
      return;
    }
    processQRCode(manualQrInput.trim());
    setManualQrInput("");
    setQrScannerOpen(false);
  };

  // Remove item
  const removeItem = (index) => {
    setReceipt((prev) => {
      if (!prev) return prev;
      const updatedItems = prev.items.filter((_, i) => i !== index);
      return { ...prev, items: updatedItems };
    });
    setSnackbarMessage("Đã xóa sản phẩm khỏi phiếu");
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
  };

  // Store select
  const handleStoreSelect = (event) => {
    const selectedStoreId = event.target.value;
    const selectedStore = stores.find(
      (store) => store.IDCH === selectedStoreId
    );
    if (selectedStore) {
      setStoreId(selectedStoreId);
      setStoreName(selectedStore.TenCuaHang);
      setReceipt((prev) =>
        prev ? { ...prev, khachHang: selectedStore.TenCuaHang } : prev
      );
      // Clear store search when a store is selected
      setStoreSearchTerm("");
    }
  };

  // Filter stores based on search term
  const filteredStores = stores.filter((store) => {
    if (!storeSearchTerm) return true;
    const searchLower = storeSearchTerm.toLowerCase();
    return (
      store.TenCuaHang.toLowerCase().includes(searchLower) ||
      store.IDCH.toString().toLowerCase().includes(searchLower)
    );
  });

  // Search
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value.toLowerCase();
    setSearchTerm(newSearchTerm);
    if (newSearchTerm && receipt && receipt.items) {
      const matchingItems = receipt.items
        .map((item, index) => {
          const maVtMatch =
            item.maVt && item.maVt.toLowerCase().includes(newSearchTerm);
          const tenMatch =
            item.ten && item.ten.toLowerCase().includes(newSearchTerm);
          if (maVtMatch || tenMatch) return { item, index };
          return null;
        })
        .filter(Boolean);
      setHighlightedItems(matchingItems);
      setCurrentHighlightIndex(matchingItems.length > 0 ? 0 : -1);
      setShowScrollButtons(matchingItems.length > 1);
      if (matchingItems.length > 0)
        setTimeout(() => scrollToHighlightedItem(0), 100);
    } else {
      setHighlightedItems([]);
      setCurrentHighlightIndex(-1);
      setShowScrollButtons(false);
    }
  };
  const scrollToHighlightedItem = (highlightIndex) => {
    if (highlightIndex < 0 || highlightIndex >= highlightedItems.length) return;
    const itemIndex = highlightedItems[highlightIndex].index;
    const tableRow = document.querySelector(
      `tr[data-item-index="${itemIndex}"]`
    );
    if (tableRow) {
      tableRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
      tableRow.classList.add("scroll-highlight");
      setTimeout(() => {
        tableRow.classList.remove("scroll-highlight");
      }, 1000);
    }
  };
  const scrollToNext = () => {
    if (highlightedItems.length === 0) return;
    const nextIndex = (currentHighlightIndex + 1) % highlightedItems.length;
    setCurrentHighlightIndex(nextIndex);
    scrollToHighlightedItem(nextIndex);
  };
  const scrollToPrevious = () => {
    if (highlightedItems.length === 0) return;
    const prevIndex =
      currentHighlightIndex === 0
        ? highlightedItems.length - 1
        : currentHighlightIndex - 1;
    setCurrentHighlightIndex(prevIndex);
    scrollToHighlightedItem(prevIndex);
  };
  const highlightMatchingItems = (item, index) => {
    if (!searchTerm) return false;
    const maVtMatch = item.maVt && item.maVt.toLowerCase().includes(searchTerm);
    const tenMatch = item.ten && item.ten.toLowerCase().includes(searchTerm);
    return maVtMatch || tenMatch;
  };

  // Confirm action (gửi phiếu/bqd)
  const confirmAction = async (button, message, isBqdAction = false) => {
    if (button.classList.contains("confirmed")) return;
    if (!storeId) {
      setErrorModalMessage("Vui lòng chọn cửa hàng trước khi xác nhận!");
      setErrorModalOpen(true);
      return;
    }
    if (!receipt.khachHang) {
      setErrorModalMessage("Vui lòng nhập tên khách hàng");
      setErrorModalOpen(true);
      return;
    }
    let totalQty = 0;
    const updatedItems = receipt.items.map((item) => {
      if (item.soLuong) {
        const qty = parseInt(item.soLuong, 10);
        if (!isNaN(qty)) totalQty += qty;
      }
      return item;
    });
    if (totalQty === 0) {
      setErrorModalMessage("Vui lòng quét QR code để thêm sản phẩm vào phiếu");
      setErrorModalOpen(true);
      return;
    }

    // Set submitting state
    setSubmitting(true);
    button.disabled = true;
    button.textContent = isBqdAction ? "Đang xử lý..." : "Đang gửi phiếu...";

    try {
      const now = new Date();
      const newTrangThai = isBqdAction ? "Đã duyệt" : "Đã gửi phiếu";

      // Prepare request body for nhap-hang API
      const requestBody = {
        phieu: {
          MaKH: storeId.toString(),
          NgayNhap: getReceiptDate(),
          TenUser: localStorage.getItem("TenUser") || "User",
          GhiChu: note || `Nhập bánh trung thu. Số: ${receipt.soPhieu}`,
          Status: 77,
          MaCH: storeId.toString(),
        },
        chitiet: updatedItems.map((item) => ({
          MaSP: item.maVt,
          TenSP: item.ten,
          SoLuong: parseInt(item.soLuong, 10) || 0,
          SoLuongTN: 0,
          SoLuongNhapDau: parseInt(item.soLuong, 10),
          GiaMua: item.giaMua || 0,
          GiamPT: 0,
          ThanhTien: parseInt(item.soLuong, 10) * (item.giaMua || 0),
          LoSX: item.loSX || "", // Use individual LoSX for each product
        })),
      };

      // Call the API
      const response = await fetch("https://doticom.vn:3000/nhap-hang", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Update UI on success
      button.textContent = "✓ Đã xác nhận";
      button.classList.add("confirmed");
      const statusDiv = button.nextElementSibling;
      statusDiv.textContent = message + "\n" + now.toLocaleString("vi-VN");
      statusDiv.style.color = "#2196F3";
      statusDiv.style.fontWeight = "bold";

      // Update receipt state
      setReceipt({
        ...receipt,
        items: updatedItems,
        tongSoLuong: totalQty,
        trangThai: newTrangThai,
        ngayXacNhan: now,
        idCuaHang: storeId,
        bqdApproved: isBqdAction ? true : false,
        bqdTimestamp: isBqdAction ? now : null,
        bqdMessage: isBqdAction ? message : null,
        lastUpdated: now,
        updateCount: 1,
      });

      // Show success message
      setSnackbarMessage(
        isBqdAction
          ? "Đã xác nhận kiểm soát BQD!"
          : "Đã gửi phiếu nhập bánh trung thu thành công!"
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error submitting receipt:", error);

      // Reset button state on error
      button.disabled = false;
      button.textContent = isBqdAction
        ? "Xác nhận kiểm soát"
        : "Xác nhận gửi phiếu";

      // Show error modal
      setErrorModalMessage(
        `Lỗi khi ${isBqdAction ? "xác nhận kiểm soát" : "gửi phiếu"}: ${
          error.message
        }`
      );
      setErrorModalOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  // Scroll to bottom button
  useEffect(() => {
    const handleScroll = () => {
      if (receipt && receipt.items && receipt.items.length > 10) {
        const scrollY = window.scrollY || window.pageYOffset;
        const windowHeight = window.innerHeight;
        const docHeight = document.documentElement.scrollHeight;
        setShowScrollToBottom(docHeight - (scrollY + windowHeight) > 120);
      } else {
        setShowScrollToBottom(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [receipt]);

  // Tự động focus vào QR input sau khi thêm sản phẩm
  useEffect(() => {
    if (storeId && qrInputRef.current) {
      qrInputRef.current.focus();
    }
  }, [receipt?.items, storeId]);

  // Xử lý nhập QR trực tiếp từ input và nhấn Enter
  const handleQuickQRInput = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      const qrValue = e.target.value.trim();
      processQRCode(qrValue);
      e.target.value = "";
      setTimeout(() => {
        if (qrInputRef.current) qrInputRef.current.focus();
      }, 100);
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',      // back camera
          width:    { ideal: 640 },       // lower resolution for faster scanning
          height:   { ideal: 480 }
        }
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().then(() => {
            setTimeout(() => startQRScanning(), 500);
          });
        };
      }
    } catch (error) {
      console.error('Camera error:', error);
      let errorMessage = 'Không thể truy cập camera.';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Quyền truy cập camera bị từ chối. Vui lòng cho phép truy cập camera và thử lại.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'Không tìm thấy camera trên thiết bị.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Trình duyệt không hỗ trợ truy cập camera.';
      }
      
      setCameraError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => {
        track.stop();
        console.log('Camera track stopped');
      });
      setCameraStream(null);
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startQRScanning = () => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    scanIntervalRef.current = setInterval(() => {
      if (
        videoRef.current?.readyState === 4 &&
        canvasRef.current
      ) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx    = canvas.getContext('2d');
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0);
        console.log('Scanning frame', canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode    = scanQRFromImageData(imageData);
        console.log('jsQR result:', qrCode);
        if (qrCode) {
          processQRCode(qrCode);
          handleCameraClose();
        }
      }
    }, 500);
  };

  // Simple QR code detection (you might want to use a proper QR library like jsQR)
  const scanQRFromImageData = (imageData) => {
    // use both inversion attempts to catch light/dark codes
    const code = jsQR(
      imageData.data,
      imageData.width,
      imageData.height,
      { inversionAttempts: 'attemptBoth' }
    );
    return code?.data ?? null;
  };

  const handleCameraOpen = () => {
    console.log('Opening camera dialog');  
    setCameraOpen(true);
    setCameraError(null);
    setCameraStream(null);
    // Start camera after dialog is fully open
    setTimeout(() => {
      startCamera();
    }, 300);
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
    stopCamera();
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  if (loading)
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Đang tải dữ liệu phiếu nhập bánh trung thu...</Typography>
      </Container>
    );
  if (apiError)
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          Có lỗi khi tải dữ liệu: {apiError}.
        </Alert>
      </Container>
    );
  if (!receipt)
    return (
      <Container maxWidth="lg">
        <Typography>Không tìm thấy phiếu nhập</Typography>
      </Container>
    );

  const day = receipt.ngay.getDate();
  const month = receipt.ngay.getMonth() + 1;
  const year = receipt.ngay.getFullYear();
  const getReceiptStatus = () =>
    receipt.trangThai === "Hoàn thành"
      ? "Hoàn thành"
      : receipt.bqdApproved
      ? "Đã duyệt"
      : "Đã gửi phiếu";

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 0 },
          "@media print": { display: "none" },
        }}
      >
        <Box display="flex" alignItems="center" sx={{ mb: { xs: 1, sm: 0 } }}>
          <IconButton
            sx={{ mr: 1, p: { xs: 1, sm: 1.5 } }}
            onClick={() => navigate("/nhaphangtt")}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="h1"
            sx={{ fontWeight: "bold", fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Phiếu điều phối bánh trung thu
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<PrintIcon />}
            variant="contained"
            color="primary"
            size="small"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            onClick={() => window.print()}
          >
            In
          </Button>
        </Box>
      </Box>
      {/* Store selection */}
      <Box
        sx={{ p: { xs: 1, sm: 2 }, mb: 2, "@media print": { display: "none" } }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Chọn cửa hàng
          </Typography>

          {/* Store search input */}
          <TextField
            fullWidth
            size="small"
            label="Tìm kiếm cửa hàng"
            placeholder="Nhập tên cửa hàng hoặc ID..."
            value={storeSearchTerm}
            onChange={(e) => setStoreSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small">
            <InputLabel id="store-select-label">Chọn cửa hàng</InputLabel>
            <Select
              labelId="store-select-label"
              value={storeId}
              label="Chọn cửa hàng"
              onChange={handleStoreSelect}
              required
            >
              <MenuItem value="">
                <em>-- Chọn cửa hàng --</em>
              </MenuItem>
              {filteredStores.map((store) => (
                <MenuItem key={store.IDCH} value={store.IDCH}>
                  {store.TenCuaHang} ({store.IDCH})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {storeSearchTerm && filteredStores.length === 0 && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 1, display: "block" }}
            >
              Không tìm thấy cửa hàng nào phù hợp
            </Typography>
          )}

          {!storeId && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              * Vui lòng chọn cửa hàng để tiếp tục
            </Typography>
          )}
        </Paper>
      </Box>
      {/* Store info */}
      {storeName && (
        <Box
          sx={{
            p: { xs: 1, sm: 1 },
            mx: { xs: 1, sm: 0 },
            mb: 2,
            backgroundColor: "#e3f2fd",
            display: "flex",
            alignItems: "center",
            borderRadius: 1,
            "@media print": { display: "none" },
          }}
        >
          <StorefrontIcon
            sx={{
              mr: 1,
              color: "#1976d2",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          />
          <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            Cửa hàng: <strong>{storeName}</strong>
            {storeId && <span> (ID: {storeId})</span>}
          </Typography>
        </Box>
      )}
      {/* QR Scanner and Search */}
      <Box
        sx={{
          p: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          "@media print": { display: "none" },
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1201,
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          borderRadius: "0 0 8px 8px",
          transition: "box-shadow 0.3s ease",
        }}
      >
        {storeId && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* QR input với nút camera */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                inputRef={qrInputRef}
                variant="outlined"
                label="Quét QR hoặc nhập mã LoSX"
                placeholder="Quét hoặc nhập mã QR/LoSX, nhấn Enter..."
                size="small"
                sx={{
                  width: { xs: "100%", sm: "320px" },
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
                onKeyDown={handleQuickQRInput}
                autoFocus
              />
              <IconButton
                color="primary"
                onClick={handleCameraOpen}
                sx={{
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderRadius: 1,
                  p: 1,
                }}
                title="Mở camera quét QR"
              >
                <CameraAltIcon />
              </IconButton>
            </Box>
            <Box sx={{ position: "relative" }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Tìm kiếm sản phẩm đã thêm"
                placeholder="Nhập mã sản phẩm hoặc tên sản phẩm..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
              {showScrollButtons && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius: 1,
                    padding: "4px 8px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.75rem", minWidth: "40px" }}
                  >
                    {currentHighlightIndex + 1}/{highlightedItems.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={scrollToPrevious}
                    sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: "1rem" } }}
                    title="Kết quả trước"
                  >
                    <ArrowBackIcon sx={{ transform: "rotate(90deg)" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={scrollToNext}
                    sx={{ p: 0.5, "& .MuiSvgIcon-root": { fontSize: "1rem" } }}
                    title="Kết quả tiếp theo"
                  >
                    <ArrowBackIcon sx={{ transform: "rotate(-90deg)" }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        )}
        {!storeId && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Vui lòng chọn cửa hàng ở trên để bắt đầu tạo phiếu nhập bánh trung
            thu
          </Alert>
        )}
      </Box>
      {/* Receipt layout */}
      {storeId && (
        <div className="receipt">
          <div className="header">
            <div className="logo">
              DOTI
              <br />
              COM
            </div>
            <div className="title">PHIẾU GIAO NHẬN BÁNH TRUNG THU</div>
            <div className="subtitle">20 20 30 90 96</div>
          </div>
          <div className="info-section">
            <div className="info-left">
              <div className="customer-row">
                <span className="customer-label">Khách hàng:</span>
                <input
                  type="text"
                  className="customer-input handwritten"
                  value={receipt.khachHang}
                  onChange={(e) =>
                    setReceipt({ ...receipt, khachHang: e.target.value })
                  }
                  placeholder="Nhập tên khách hàng"
                  disabled={!!storeId} /* disable khi đã chọn cửa hàng */
                  style={{
                    backgroundColor: storeId ? "#f5f5f5" : "transparent",
                    cursor: storeId ? "not-allowed" : "text",
                  }}
                />
              </div>
              <div className="date-row">
                Ngày: <span className="handwritten">{day}</span> tháng{" "}
                <span className="handwritten">{month}</span> năm{" "}
                <span className="handwritten">{year}</span>
              </div>
            </div>
            <div className="info-right">
              <div>Ký hiệu: DTTT/BN</div>
              <div>Số: {receipt.soPhieu.split("-")[1]}</div>
            </div>
          </div>
          <div className="table-container">
            <table
              className="items-table"
              style={{ tableLayout: "fixed", width: "100%" }}
            >
              <thead>
                <tr>
                  <th className="code-col" style={{ width: "15%" }}>
                    Mã vt
                  </th>
                  <th className="name-col" style={{ width: "50%" }}>
                    Tên Mặt Hàng
                  </th>
                  <th className="qty-col" style={{ width: "15%" }}>
                    Số lượng
                  </th>
                  <th className="action-col" style={{ width: "20%" }}>
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                {receipt.items.length > 0 ? (
                  receipt.items.map((item, index) => {
                    const isHighlighted = highlightMatchingItems(item, index);
                    const isCurrentHighlight =
                      highlightedItems.length > 0 &&
                      highlightedItems[currentHighlightIndex]?.index === index;
                    return (
                      <tr key={`item-${index}`} data-item-index={index}>
                        <td
                          className={`code-cell ${
                            isHighlighted ? "highlight" : ""
                          } ${isCurrentHighlight ? "current-highlight" : ""}`}
                        >
                          {item.maVt}
                        </td>
                        <td
                          className={`name-cell ${
                            isHighlighted ? "highlight" : ""
                          } ${isCurrentHighlight ? "current-highlight" : ""}`}
                        >
                          {item.ten}
                        </td>
                        <td className="handwritten qty-cell">
                          <input
                            type="number"
                            inputMode="numeric"
                            value={item.soLuong || ""}
                            onChange={(e) => {
                              const newItems = [...receipt.items];
                              newItems[index] = {
                                ...newItems[index],
                                soLuong: e.target.value,
                              };
                              setReceipt({ ...receipt, items: newItems });
                            }}
                            className={`qty-input ${
                              isHighlighted ? "highlight" : ""
                            } ${isCurrentHighlight ? "current-highlight" : ""}`}
                            title="Số lượng"
                            min="1"
                          />
                        </td>
                        <td className="action-cell">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => removeItem(index)}
                            title="Xóa sản phẩm"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        fontStyle: "italic",
                        color: "#666",
                      }}
                    >
                      Chưa có sản phẩm nào. Vui lòng quét QR code để thêm sản
                      phẩm.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="total-section">
            <div className="status-row">
              <div className="status-item">
                <strong>Trạng thái: </strong>
                <span
                  className={`status-badge ${getReceiptStatus()
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                >
                  {getReceiptStatus()}
                </span>
              </div>
              <div className="rdb-item">
                <strong>
                  Tổng số lượng:{" "}
                  {receipt.items.reduce(
                    (sum, item) => sum + (parseInt(item.soLuong) || 0),
                    0
                  )}
                </strong>
              </div>
            </div>
          </div>
          <div className="signature-section">
            {isAdmin && (
              <div className="signature-box">
                <div className="signature-title">
                  Kiểm soát BQD
                  <br />
                  (Ký, ghi rõ họ tên)
                </div>
                <button
                  className="confirm-btn"
                  onClick={(e) =>
                    confirmAction(e.target, "Đã kiểm soát BQD", true)
                  }
                  disabled={submitting}
                >
                  {submitting ? "Đang xử lý..." : "Xác nhận kiểm soát"}
                </button>
                <div className="status-text"></div>
              </div>
            )}
            <div className="signature-box">
              <div className="signature-title">Điều phối</div>
              <button
                className="confirm-btn"
                onClick={(e) =>
                  confirmAction(e.target, "Đã xác nhận gửi phiếu")
                }
                disabled={submitting}
              >
                {submitting ? "Đang gửi phiếu..." : "Xác nhận gửi phiếu"}
              </button>
              <div className="status-text"></div>
            </div>
          </div>
        </div>
      )}
      {/* QR Scanner Dialog */}
      <Dialog
        open={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Quét QR Code bánh trung thu</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nhập URL QR code hoặc mã LoSX:
            </Typography>
            <TextField
              fullWidth
              label="QR Code / LoSX"
              value={manualQrInput}
              onChange={(e) => setManualQrInput(e.target.value)}
              placeholder="https://doticom.vn/t.php?c=368 hoặc 368"
              size="small"
              onKeyPress={(e) => {
                if (e.key === "Enter") handleManualQRSubmit();
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Ví dụ: https://doticom.vn/t.php?c=368 hoặc chỉ cần nhập 368
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrScannerOpen(false)} color="inherit">
            Hủy bỏ
          </Button>
          <Button
            onClick={handleManualQRSubmit}
            variant="contained"
            color="primary"
            disabled={!manualQrInput.trim() || qrProcessing}
          >
            {qrProcessing ? "Đang xử lý..." : "Thêm sản phẩm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera QR Scanner Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={handleCameraClose}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 600}
      >
        <DialogTitle>Quét QR Code bằng Camera</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {cameraError && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {cameraError}
            </Alert>
          )}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 300,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#f0f0f0",
              border: "2px dashed #1976d2",
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              autoPlay
              playsInline
            />
            <canvas
              ref={canvasRef}
              style={{
                display: "none",
                width: "100%",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            <QrCodeScannerIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                fontSize: "3rem",
                color: "#1976d2",
                animation: "spin 2s linear infinite",
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" align="center">
            Đưa mã QR vào khung hình để quét.{" "}
            <strong>
              (Lưu ý: Sử dụng camera sau để có kết quả tốt nhất)
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCameraClose} color="inherit">
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Style block must come after all JSX elements */}
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          background-color: #f5f5f5;
          padding: 20px;
        }

        .receipt {
          max-width: 1200px;
          margin: 0 auto;
          background: white;
          padding: 20px;
          border: 2px solid #333;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .header {
          text-align: center;
          margin-bottom: 20px;
          position: relative;
        }

        .logo {
          position: absolute;
          left: 0;
          top: 0;
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #ff6b35, #f7931e);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        }

        .title {
          color: #d32f2f;
          font-size: 28px;
          font-weight: bold;
          margin-top: 10px;
          letter-spacing: 2px;
        }

        .subtitle {
          font-style: italic;
          color: #666;
          margin-top: 5px;
        }

        .info-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }

        .info-left {
          flex: 1;
        }

        .info-right {
          text-align: right;
        }

        .customer-row {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 8px;
        }

        .customer-label {
          font-weight: bold;
        }

        .date-row {
          font-size: 11px;
        }

        .table-container {
          overflow-x: hidden;
          margin-bottom: 20px;
          width: 100%;
          -webkit-overflow-scrolling: auto;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
          table-layout: fixed !important;
          max-width: 100%;
        }

        .items-table th,
        .items-table td {
          border: 1px solid #333;
          padding: 6px 4px;
          text-align: left;
          word-wrap: break-word;
          vertical-align: top;
          overflow: visible;
          height: auto;
          min-height: 35px;
        }

        .items-table th {
          background-color: #ffcccb;
          font-weight: bold;
          text-align: center;
          position: sticky;
          top: 0;
          z-index: 10;
          font-size: 10px;
          height: 35px;
          line-height: 1.3;
          padding: 8px 4px;
          vertical-align: middle;
        }

        .code-cell {
          font-size: 14px;
          word-break: break-all;
          overflow: visible;
          white-space: normal;
          line-height: 1.2;
          vertical-align: top;
        }

        .name-cell {
          word-break: break-word;
          overflow: visible !important;
          text-overflow: initial !important;
          white-space: normal !important;
          font-size: 14px;
          line-height: 1.3;
          padding: 6px 4px !important;
          vertical-align: top;
          max-height: none !important;
          -webkit-line-clamp: none !important;
          -webkit-box-orient: initial !important;
          display: table-cell !important;
        }

        .qty-cell {
          text-align: center;
          vertical-align: middle;
          padding: 6px 4px !important; /* đảm bảo có padding để căn giữa */
        }

        .action-cell {
          text-align: center;
          vertical-align: middle;
        }

        .signature-section {
          display: flex;
          flex-direction: column;
          gap: 15px;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 2px solid #333;
        }

        .signature-box {
          text-align: center;
          padding: 15px 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #f9f9f9;
        }

        .signature-title {
          font-weight: bold;
          margin-bottom: 12px;
          color: #333;
          font-size: 11px;
          line-height: 1.3;
        }

        .confirm-btn {
          background: linear-gradient(45deg, #4caf50, #45a049);
          color: white;
          border: none;
          padding: 12px 16px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          width: 100%;
          min-height: 48px;
          touch-action: manipulation;
        }

        .confirm-btn:hover {
          background: linear-gradient(45deg, #45a049, #4caf50);
          transform: translateY(-1px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
        }

        .confirm-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .confirm-btn:disabled {
          background: #cccccc !important;
          cursor: not-allowed !important;
          transform: none !important;
          box-shadow: none !important;
        }

        .confirm-btn.confirmed {
          background: linear-gradient(45deg, #2196f3, #1976d2);
          cursor: default;
        }

        .confirm-btn.confirmed:hover {
          transform: none;
          background: linear-gradient(45deg, #2196f3, #1976d2);
        }

        .status-text {
          font-size: 10px;
          margin-top: 8px;
          color: #666;
          font-style: italic;
          line-height: 1.2;
        }

        .total-section {
          margin-top: 15px;
          padding: 10px;
          background-color: #f0f0f0;
          border-radius: 5px;
        }

        .status-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-start;
        }

        .status-item {
          font-size: 11px;
        }

        .rdb-item {
          font-size: 11px;
          align-self: flex-end;
        }

        .highlight {
          background-color: #fffde7;
          box-shadow: 0 0 5px rgba(255, 193, 7, 0.5);
          font-weight: bold;
        }

        .current-highlight {
          background-color: #fff3c4 !important;
          box-shadow: 0 0 10px rgba(255, 152, 0, 0.8) !important;
          border: 2px solid #ff9800 !important;
        }

        .scroll-highlight {
          animation: pulseHighlight 1s ease-in-out;
        }

        @keyframes pulseHighlight {
          0% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 20px rgba(255, 152, 0, 1);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(255, 152, 0, 0.8);
          }
        }

        input.highlight {
          background-color: #fffde7;
          border-bottom: 2px solid #ffc107 !important;
        }

        input.current-highlight {
          background-color: #fff3c4 !important;
          border-bottom: 2px solid #ff9800 !important;
        }

        /* Very small mobile screens (width <= 360px) */
        @media (max-width: 360px) {
          body {
            padding: 4px;
            overflow-x: hidden;
          }

          .receipt {
            padding: 8px;
            border-width: 1px;
            overflow-x: hidden;
          }

          .logo {
            width: 40px;
            height: 40px;
            font-size: 8px;
          }

          .title {
            font-size: 14px;
            letter-spacing: 0.5px;
            margin-left: 45px;
          }

          .subtitle {
            font-size: 10px;
          }

          .info-section {
            font-size: 10px;
            margin-bottom: 12px;
            padding-bottom: 8px;
            gap: 6px;
          }

          .customer-row {
            gap: 3px;
            margin-bottom: 6px;
          }

          .customer-input {
            font-size: 10px;
            padding: 3px 6px;
          }

          .date-row {
            font-size: 9px;
          }

          .info-right {
            font-size: 10px;
          }

          .table-container {
            margin-bottom: 15px;
            overflow-x: hidden;
          }

          .items-table {
            font-size: 8px;
            table-layout: fixed;
            width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 4px 2px;
            overflow: visible;
            height: auto;
            min-height: 30px;
          }

          .name-cell {
            font-size: 9px;
            line-height: 1.2;
            padding: 4px 2px !important;
          }

          .qty-input {
            width: 50px; /* nhỏ hơn trên mobile */
            font-size: 10px;
            min-height: 24px;
          }

          /* Tablet styles */
          @media (min-width: 768px) {
            body {
              padding: 20px;
              overflow-x: hidden;
            }

            .receipt {
              padding: 20px;
              overflow-x: hidden;
            }

            .logo {
              width: 60px;
              height: 60px;
              font-size: 12px;
            }

            .title {
              font-size: 24px;
            }

            .subtitle {
              font-size: 14px;
            }

            .info-section {
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              font-size: 14px;
            }

            .customer-row {
              flex-direction: row;
              align-items: center;
              gap: 8px;
            }

            .table-container {
              overflow-x: hidden;
            }

            .items-table th,
            .items-table td {
              padding: 8px 6px;
              min-height: 40px;
            }

            .name-cell {
              font-size: 11px;
              line-height: 1.4;
            }

            .qty-input {
              width: 80px; /* lớn hơn một chút trên tablet */
              font-size: 12px;
            }

            .signature-section {
              flex-direction: row;
              justify-content: space-around;
            }

            .signature-title {
              font-size: 13px;
            }

            .confirm-btn {
              font-size: 14px;
            }

            .status-row {
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            }

            .status-item,
            .rdb-item {
              font-size: 13px;
            }
          }

          /* Desktop styles */
          @media (min-width: 1024px) {
            .receipt {
              max-width: 1200px;
              overflow-x: hidden;
            }

            .title {
              font-size: 28px;
            }

            .items-table {
              font-size: 12px;
            }

            .signature-section {
              gap: 20px;
            }
          }

          @media print {
            body {
              padding: 0;
              overflow-x: hidden;
            }
            .receipt {
              max-width: 100%;
              border: none;
              box-shadow: none;
              padding: 10px;
              overflow-x: hidden;
            }
            .qty-input {
              border-bottom: 1px solid #999;
            }
            .signature-section {
              flex-direction: row;
            }
            .table-container {
              overflow-x: hidden;
            }
          }

          /* Global prevent horizontal scroll */
          html,
          body {
            overflow-x: hidden;
            max-width: 100vw;
          }

          * {
            box-sizing: border-box;
          }

          .MuiContainer-root {
            overflow-x: hidden !important;
          }

          .info-section {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
          }

          .customer-row {
            flex-direction: row;
            align-items: center;
            gap: 8px;
          }

          .table-container {
            overflow-x: hidden;
          }

          /* SỬA: Thêm width cho action-col trong tablet */
          .code-col,
          .code-cell {
            width: 15% !important;
          }

          .name-col,
          .name-cell {
            width: 50% !important;
          }

          .qty-col,
          .qty-cell {
            width: 15% !important;
          }

          .action-col,
          .action-cell {
            width: 20% !important;
          }

          .signature-section {
            flex-direction: row;
            justify-content: space-around;
          }

          .signature-title {
            font-size: 13px;
          }

          .confirm-btn {
            font-size: 14px;
          }

          .status-row {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }

          .status-item,
          .rdb-item {
            font-size: 13px;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .receipt {
            max-width: 1200px;
            overflow-x: hidden;
          }

          .title {
            font-size: 28px;
          }

          .items-table {
            font-size: 12px;
          }

          .signature-section {
            gap: 20px;
          }
        }

        @media print {
          body {
            padding: 0;
            overflow-x: hidden;
          }
          .receipt {
            max-width: 100%;
            border: none;
            box-shadow: none;
            padding: 10px;
            overflow-x: hidden;
          }
          .qty-input {
            border-bottom: 1px solid #999;
          }
          .signature-section {
            flex-direction: row;
          }
          .table-container {
            overflow-x: hidden;
          }
        }

        /* Global prevent horizontal scroll */
        html,
        body {
          overflow-x: hidden;
          max-width: 100vw;
        }

        * {
          box-sizing: border-box;
        }

        .MuiContainer-root {
          overflow-x: hidden !important;
        }

        .qty-input {
          width: 70px; /* giảm từ 100% xuống 70px */
          border: none;
          background: transparent;
          border-bottom: 1px dashed #999;
          color: #0066cc;
          font-weight: bold;
          text-align: center;
          padding: 4px 2px;
          font-family: "Courier New", monospace;
          font-size: 11px;
          min-height: 28px;
          -webkit-appearance: none;
          -moz-appearance: textfield;
          margin: 0 auto; /* căn giữa */
          display: block; /* để margin auto hoạt động */
        }

        .customer-input {
          border: none;
          border-bottom: 1px dashed #999;
          outline: none;
          padding: 4px 8px;
          width: 100%;
          margin-top: 4px;
          font-size: 12px;
        }

        .customer-input:disabled {
          background-color: #f5f5f5 !important;
          color: #666 !important;
          cursor: not-allowed !important;
        }
      `}</style>
    </Box>
  );
};

export default NewDieuChuyenTT;
  
